import React, { useMemo } from "react"

import { ChartFigure } from "@coval/ui/chart-figure"
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { PRIMARY_BAR_SERIES_COLOR } from "@/components/charts/seriesPalette"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import {
  buildDistributionTable,
  summarizeDistribution,
} from "./DistributionFigure"
import {
  createHistogramBins,
  formatBinRange,
  type HistogramBin,
} from "./HistogramChart"

interface MiniDistributionChartProps {
  metricOutputs: RunMetricOutput[]
  height?: number
  xAxisLabel?: string
  /** When provided, use fixed-width bins (e.g. 30 for 30-second increments). Values in seconds. */
  binWidthSeconds?: number
  /**
   * When provided for float output type, use these values instead of float_value.
   * Allows metrics (e.g. Latency) to extract multiple values per output from result_json.
   */
  extractFloatValues?: (metricOutputs: RunMetricOutput[]) => number[]
  /** When provided, highlight the bar containing this value and dim all others. */
  highlightValue?: number | string | null
}

interface CategoricalDataPoint {
  label: string
  count: number
}

const MAX_BINS = 12

const formatSecondsToMinSec = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

/**
 * Create fixed-width histogram bins (e.g. 30-second increments), capped at MAX_BINS.
 */
const createFixedWidthBins = (
  values: number[],
  binWidth: number,
): HistogramBin[] => {
  if (values.length === 0 || binWidth <= 0) {
    return []
  }
  const minVal = Math.min(...values)
  const maxVal = Math.max(...values)
  const range = maxVal - minVal
  let firstBinStart = Math.floor(minVal / binWidth) * binWidth
  let numBins = Math.ceil((maxVal - firstBinStart) / binWidth) || 1
  if (numBins > MAX_BINS) {
    binWidth = Math.max(binWidth, Math.ceil(range / MAX_BINS))
    firstBinStart = Math.floor(minVal / binWidth) * binWidth
    numBins = Math.min(
      MAX_BINS,
      Math.ceil((maxVal - firstBinStart) / binWidth) || 1,
    )
  }
  if (numBins <= 0) {
    return [
      {
        label: firstBinStart.toString(),
        count: values.length,
        min: firstBinStart,
        max: firstBinStart + binWidth,
      },
    ]
  }
  const bins: HistogramBin[] = Array.from({ length: numBins }, (_, i) => ({
    label: (firstBinStart + i * binWidth).toString(),
    count: 0,
    min: firstBinStart + i * binWidth,
    max: firstBinStart + (i + 1) * binWidth,
  }))
  values.forEach((value) => {
    let binIndex = Math.floor((value - firstBinStart) / binWidth)
    if (binIndex >= numBins) {
      binIndex = numBins - 1
    } else if (binIndex < 0) {
      binIndex = 0
    }
    bins[binIndex].count++
  })
  return bins
}

/**
 * A compact mini bar chart for displaying distribution in metric cards.
 * Designed to fit in limited card space without axes or labels.
 */
export const MiniDistributionChart: React.FC<MiniDistributionChartProps> = ({
  metricOutputs,
  height = 164,
  xAxisLabel = "Value",
  binWidthSeconds,
  extractFloatValues,
  highlightValue,
}) => {
  const chartData = useMemo(() => {
    if (metricOutputs.length === 0) {
      return null
    }

    const outputType = metricOutputs[0]?.output_type

    if (outputType === "float") {
      // Extract float values - use custom extractor when provided (e.g. Latency uses result_json)
      const floatValues = extractFloatValues
        ? extractFloatValues(metricOutputs)
        : metricOutputs
            .map((mo) => mo.float_value)
            .filter((val): val is number => val != null && Number.isFinite(val))

      if (floatValues.length === 0) {
        return null
      }

      const bins =
        binWidthSeconds != null && binWidthSeconds > 0
          ? createFixedWidthBins(floatValues, binWidthSeconds)
          : createHistogramBins(floatValues)
      return {
        type: "histogram" as const,
        data: bins.map((bin) => ({
          label: bin.label,
          count: bin.count,
          min: bin.min,
          max: bin.max,
        })),
      }
    } else if (outputType === "string") {
      // Count occurrences of each string value
      const valueCounts = metricOutputs.reduce(
        (acc: Record<string, number>, curr) => {
          if (curr.string_value) {
            const normalizedValue = curr.string_value.trim()
            acc[normalizedValue] = (acc[normalizedValue] || 0) + 1
          }
          return acc
        },
        {},
      )

      const data: CategoricalDataPoint[] = Object.entries(valueCounts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count) // Sort by count descending
        .slice(0, 10) // Limit to top 10 for mini chart
        .map(({ value, count }) => ({
          label: value,
          count,
        }))

      return {
        type: "categorical" as const,
        data,
        totals: {
          count: Object.values(valueCounts).reduce((sum, n) => sum + n, 0),
          groups: Object.keys(valueCounts).length,
        },
      }
    } else if (outputType === "set") {
      // For set values, count occurrences of each unique item across all sets
      const valueCounts = metricOutputs.reduce(
        (acc: Record<string, number>, curr) => {
          if (curr.set_value && Array.isArray(curr.set_value)) {
            curr.set_value.forEach((item) => {
              acc[item] = (acc[item] || 0) + 1
            })
          }
          return acc
        },
        {},
      )

      const data: CategoricalDataPoint[] = Object.entries(valueCounts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count) // Sort by count descending
        .slice(0, 10) // Limit to top 10 for mini chart
        .map(({ value, count }) => ({
          label: value,
          count,
        }))

      return {
        type: "categorical" as const,
        data,
        totals: {
          count: Object.values(valueCounts).reduce((sum, n) => sum + n, 0),
          groups: Object.keys(valueCounts).length,
        },
      }
    }

    return null
  }, [metricOutputs, binWidthSeconds, extractFloatValues])

  // Calculate explicit Y-axis ticks to ensure grid lines align with labels
  // Target 3-4 ticks for mini chart readability
  const yAxisTicks = useMemo(() => {
    if (!chartData || chartData.data.length === 0) {
      return [0]
    }
    const maxCount = Math.max(...chartData.data.map((d) => d.count))
    if (maxCount <= 0) {
      return [0]
    }

    // Lookup table for step sizes: [maxCountLimit, stepSize]
    const STEPS: Array<[number, number]> = [
      [3, 1],
      [6, 2],
      [15, 5],
      [30, 10],
      [60, 20],
      [150, 50],
    ]

    const step =
      STEPS.find(([limit]) => maxCount <= limit)?.[1] ??
      (() => {
        // For larger counts, calculate a nice step
        const magnitude = 10 ** Math.floor(Math.log10(maxCount / 3))
        return Math.ceil(maxCount / 3 / magnitude) * magnitude
      })()

    // Generate ticks from 0 up to or past maxCount
    const ticks: number[] = []
    for (let tick = 0; tick <= maxCount; tick += step) {
      ticks.push(tick)
    }
    // Ensure the last tick covers the max count
    if (ticks[ticks.length - 1] < maxCount) {
      ticks.push(ticks[ticks.length - 1] + step)
    }
    return ticks
  }, [chartData])

  const highlightedIndex = useMemo(() => {
    if (highlightValue == null || !chartData) {
      return null
    }

    if (chartData.type === "histogram" && typeof highlightValue === "number") {
      const bins = chartData.data as Array<{ min: number; max: number }>
      const lastIndex = bins.length - 1
      const idx = bins.findIndex((bin, i) =>
        i === lastIndex
          ? highlightValue >= bin.min && highlightValue <= bin.max
          : highlightValue >= bin.min && highlightValue < bin.max,
      )
      return idx >= 0 ? idx : null
    }

    if (chartData.type === "categorical") {
      const idx = chartData.data.findIndex(
        (d) => d.label === String(highlightValue),
      )
      return idx >= 0 ? idx : null
    }

    return null
  }, [chartData, highlightValue])

  const rows = useMemo(
    () =>
      (chartData?.data ?? []).map((item) => {
        if ("min" in item && "max" in item) {
          const bin = item as { min: number; max: number; label: string }
          return {
            label:
              binWidthSeconds != null
                ? `${formatSecondsToMinSec(bin.min)} – ${formatSecondsToMinSec(bin.max)}`
                : formatBinRange(bin.min, bin.max, bin.label),
            count: item.count,
          }
        }
        return { label: item.label, count: item.count }
      }),
    [binWidthSeconds, chartData],
  )

  if (!chartData || chartData.data.length === 0) {
    return null
  }

  // Don't show chart if there's only one data point
  if (chartData.data.length <= 1) {
    return null
  }

  // Theme-aware colors for dark mode support
  const barColor = PRIMARY_BAR_SERIES_COLOR
  // Use default text color for axis labels
  const textColor = "currentColor"
  // Gray color for data tick labels (matches detail view)
  const tickLabelColor = "var(--coval-text-muted)"
  // Use divider color for axis lines (matches border-coval-border / card border)
  const dividerColor = "var(--coval-border)"

  // Format tick label for x-axis - whole numbers or M:SS when binWidthSeconds (e.g. audio duration)
  const formatXAxisTick = (value: string, index: number) => {
    if (chartData.type === "histogram") {
      const item = chartData.data[index]
      if (item && "min" in item) {
        const minVal = (item as { min: number }).min
        return binWidthSeconds != null
          ? formatSecondsToMinSec(minVal)
          : (item as { label: string }).label
      }
    }
    // For categorical, truncate long labels
    if (value.length > 8) {
      return `${value.slice(0, 7)}…`
    }
    return value
  }

  const label = `Distribution of ${xAxisLabel}`
  const totals = "totals" in chartData ? chartData.totals : undefined

  return (
    <ChartFigure
      label={label}
      summary={summarizeDistribution(rows, highlightedIndex, totals)}
      table={buildDistributionTable(rows, {
        valueHeader: xAxisLabel,
        highlightIndex: highlightedIndex,
        total: totals?.count,
      })}
      className="w-full mt-1"
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          role="img"
          aria-label={label}
          data={chartData.data}
          margin={{ top: 5, right: 5, left: 10, bottom: 10 }}
          barCategoryGap="8%"
          barGap={0}
        >
          {/* Draw horizontal grid lines at each Y-axis tick for perfect alignment */}
          {yAxisTicks.map((tick) => (
            <ReferenceLine
              key={tick}
              y={tick}
              stroke={dividerColor}
              strokeWidth={1}
            />
          ))}
          <XAxis
            dataKey="label"
            axisLine={{ stroke: dividerColor, strokeWidth: 1 }}
            tickLine={false}
            tick={{ fontSize: 10, fill: tickLabelColor }}
            tickFormatter={formatXAxisTick}
            interval={0}
            height={30}
            label={{
              value: xAxisLabel,
              position: "insideBottom",
              offset: -5,
              style: { fontSize: 11, fill: textColor, fontWeight: 600 },
            }}
          />
          <YAxis
            axisLine={{ stroke: dividerColor, strokeWidth: 1 }}
            tickLine={false}
            tick={{ fontSize: 10, fill: tickLabelColor }}
            width={45}
            domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
            ticks={yAxisTicks}
            allowDecimals={false}
            label={{
              value: "Count",
              angle: -90,
              position: "insideLeft",
              offset: 20,
              viewBox: { x: 0, y: 0, width: 232, height: 100 },
              style: {
                fontSize: 11,
                fill: textColor,
                textAnchor: "middle",
                fontWeight: 600,
              },
            }}
          />
          <Tooltip
            formatter={(value: number) => [value, "Count"]}
            labelFormatter={(label, payload) => {
              if (chartData.type === "histogram" && payload?.[0]?.payload) {
                const item = payload[0].payload as {
                  min: number
                  max: number
                  label: string
                }
                return binWidthSeconds != null
                  ? `Range: ${formatSecondsToMinSec(item.min)} – ${formatSecondsToMinSec(item.max)}`
                  : formatBinRange(item.min, item.max, item.label)
              }
              return chartData.type === "histogram"
                ? `Range: ${label}`
                : `${label}`
            }}
            contentStyle={{
              borderRadius: "6px",
              fontSize: "12px",
              padding: "4px 8px",
              backgroundColor: "var(--surface-default)",
              border: `1px solid ${dividerColor}`,
              color: "var(--coval-text)",
            }}
            labelStyle={{
              color: "var(--coval-text)",
            }}
            itemStyle={{
              color: "var(--coval-text)",
            }}
            cursor={{ fill: dividerColor }}
          />
          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {chartData.data.map((_, index) => (
              <Cell
                key={index}
                fill={barColor}
                opacity={
                  highlightedIndex != null && index !== highlightedIndex
                    ? 0.4
                    : 1
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFigure>
  )
}
